/**
 * Hasta Uygulaması - Ana Sayfa (Landing)
 * Kayıt ve giriş seçenekleri
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Baby, 
  Sparkles, 
  Heart, 
  Camera,
  ArrowRight,
  Mail,
  Lock,
  User,
  Phone,
  Calendar,
  Loader2,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientLandingPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('landing'); // landing, login, register
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    pregnancy_week: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/patient-app/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        localStorage.setItem('patient_token', data.access_token);
        localStorage.setItem('patient_user', JSON.stringify(data.user));
        toast.success('Giris basarili!');
        if (data.email_verified === false || data.user?.email_verified === false) {
          window.location.href = '/patient/verify';
        } else {
          window.location.href = '/patient/dashboard';
        }
      } else {
        toast.error(data.detail || 'Giris hatasi');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/patient-app/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone || null,
          pregnancy_week: formData.pregnancy_week ? parseInt(formData.pregnancy_week) : null
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        localStorage.setItem('patient_token', data.access_token);
        localStorage.setItem('patient_user', JSON.stringify(data.user));
        toast.success('Kayit basarili! Email adresinize dogrulama kodu gonderildi.');
        window.location.href = '/patient/verify';
      } else {
        toast.error(data.detail || 'Kayit hatasi');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  // Landing sayfası
  if (mode === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        {/* Header */}
        <header className="p-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Baby className="w-8 h-8 text-pink-500" />
            <span className="font-bold text-xl text-slate-800">BabyFace AI</span>
          </div>
          <Button variant="ghost" onClick={() => setMode('login')}>
            Giriş Yap
          </Button>
        </header>

        {/* Hero */}
        <main className="container mx-auto px-4 py-12">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Yapay Zeka ile Bebek Yüzü
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
              Bebeğinizin Yüzünü 
              <span className="text-pink-500"> Şimdiden Görün</span>
            </h1>
            
            <p className="text-lg text-slate-600 mb-8">
              Ultrason görüntünüzü yükleyin, yapay zeka bebeğinizin gerçekçi 4K yüzünü oluştursun. 
              Hamileliğin en güzel anısı!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg" 
                className="bg-pink-500 hover:bg-pink-600 text-lg px-8"
                onClick={() => setMode('register')}
              >
                Ücretsiz Başla
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => setMode('login')}
              >
                Giriş Yap
              </Button>
            </div>

            {/* Features */}
            <div className="grid md:grid-cols-3 gap-6 mt-16">
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Camera className="w-6 h-6 text-pink-500" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Ultrason Yükle</h3>
                <p className="text-sm text-slate-500">Doktorunuzdan aldığınız ultrason görüntüsünü yükleyin</p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">AI Üretimi</h3>
                <p className="text-sm text-slate-500">Yapay zeka bebeğinizin yüzünü saniyeler içinde oluşturur</p>
              </div>
              
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Heart className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">Paylaş & Sakla</h3>
                <p className="text-sm text-slate-500">Ailenizle paylaşın, hatıra olarak saklayın</p>
              </div>
            </div>

            {/* Social Proof */}
            <div className="mt-16 flex items-center justify-center gap-2 text-slate-500">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>10.000+ mutlu anne</span>
              <span className="mx-2">•</span>
              <span>4.9 puan</span>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer data-testid="landing-footer" className="bg-white/60 backdrop-blur border-t mt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Baby className="w-6 h-6 text-pink-500" />
                  <span className="font-bold text-slate-800">BabyFace AI</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  KONSULTAN OZEL SAGLIK HIZMETLERI GIDA HAYV. TURZ. SAN. VE TIC. LTD. STI.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-3 text-sm">Kurumsal</h4>
                <ul className="space-y-2">
                  <li>
                    <Link data-testid="footer-about-link" to="/patient/hakkimizda" className="text-sm text-slate-500 hover:text-pink-500 transition-colors">
                      Hakkimizda
                    </Link>
                  </li>
                  <li>
                    <Link data-testid="footer-contact-link" to="/patient/iletisim" className="text-sm text-slate-500 hover:text-pink-500 transition-colors">
                      Iletisim
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-slate-700 mb-3 text-sm">Sozlesmeler</h4>
                <ul className="space-y-2">
                  <li>
                    <Link data-testid="footer-agreement-link" to="/patient/sozlesme" className="text-sm text-slate-500 hover:text-pink-500 transition-colors">
                      Kullanici Sozlesmesi
                    </Link>
                  </li>
                  <li>
                    <Link data-testid="footer-privacy-link" to="/patient/gizlilik" className="text-sm text-slate-500 hover:text-pink-500 transition-colors">
                      Gizlilik Politikasi / KVKK
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t mt-6 pt-4 text-center">
              <p className="text-xs text-slate-400">
                &copy; 2026 BabyFace AI - Tum haklari saklidir.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Giriş Formu
  if (mode === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Baby className="w-8 h-8 text-pink-500" />
              <span className="font-bold text-xl text-slate-800">BabyFace AI</span>
            </div>
            <CardTitle>Giriş Yap</CardTitle>
            <CardDescription>Hesabınıza giriş yapın</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-600" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Giriş Yap
              </Button>
              
              <p className="text-center text-sm text-slate-500">
                Hesabınız yok mu?{' '}
                <button type="button" onClick={() => setMode('register')} className="text-pink-500 hover:underline">
                  Kayıt Ol
                </button>
              </p>
              
              <button type="button" onClick={() => setMode('landing')} className="w-full text-sm text-slate-400 hover:text-slate-600">
                ← Ana Sayfaya Dön
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Kayıt Formu
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Baby className="w-8 h-8 text-pink-500" />
            <span className="font-bold text-xl text-slate-800">BabyFace AI</span>
          </div>
          <CardTitle>Ücretsiz Kayıt Ol</CardTitle>
          <CardDescription>İlk üretim ücretsiz! 🎁</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name">Ad</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="Adınız"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Soyad</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  placeholder="Soyadınız"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="En az 6 karakter"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="pl-10"
                  minLength={6}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon (opsiyonel)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="0532..."
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pregnancy_week">Gebelik Haftası</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="pregnancy_week"
                    name="pregnancy_week"
                    type="number"
                    min="4"
                    max="42"
                    placeholder="Hafta"
                    value={formData.pregnancy_week}
                    onChange={handleInputChange}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-600" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Ücretsiz Kayıt Ol
            </Button>
            
            <p className="text-center text-sm text-slate-500">
              Zaten hesabınız var mı?{' '}
              <button type="button" onClick={() => setMode('login')} className="text-pink-500 hover:underline">
                Giriş Yap
              </button>
            </p>
            
            <button type="button" onClick={() => setMode('landing')} className="w-full text-sm text-slate-400 hover:text-slate-600">
              ← Ana Sayfaya Dön
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientLandingPage;
